import { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function InvestPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [selectedInvestment, setSelectedInvestment] = useState<string | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });

  const investments = [
    {
      key: 'agricultural',
      icon: 'ri-plant-line',
      image: '/agriculture.png',
      color: 'from-green-600 to-green-800',
    },
    {
      key: 'tourist',
      icon: 'ri-hotel-line',
      image: '/tourist.png',
      color: 'from-blue-600 to-blue-800',
    },
    {
      key: 'healthcare',
      icon: 'ri-hospital-line',
      image: '/healthcare.png',
      color: 'from-red-600 to-red-800',
    },
    {
      key: 'commercial',
      icon: 'ri-store-line',
      image: '/commercial.png',
      color: 'from-purple-600 to-purple-800',
    },
    {
      key: 'technological',
      icon: 'ri-computer-line',
      image: '/technology.png',
      color: 'from-cyan-600 to-cyan-800',
    },
    {
      key: 'energy',
      icon: 'ri-flashlight-line',
      image: '/energy.png',
      color: 'from-yellow-600 to-yellow-800',
    },
    {
      key: 'oil',
      icon: 'ri-oil-line',
      image: '/oilgas.png',
      color: 'from-gray-600 to-gray-800',
    },
    {
      key: 'industrial',
      icon: 'ri-building-4-line',
      image: '/industrial.png',
      color: 'from-orange-600 to-orange-800',
    },
    {
      key: 'educational',
      icon: 'ri-graduation-cap-line',
      image: '/education.png',
      color: 'from-indigo-600 to-indigo-800',
    },
  ];

  // Auto play slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % investments.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [investments.length]);

  const handleInvestmentClick = (key: string) => {
    setSelectedInvestment(key);
    setShowPopup(true);
    setIsSubmitted(false);
    setFormData({ name: '', email: '', company: '', message: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await emailjs.send(
        'service_3f6oc3n',
        'template_4pylorr',
        {
          from_name: formData.name,
          from_email: formData.email,
          company: formData.company,
          investment_type: selectedInvestment ? t(`investPage.investments.${selectedInvestment}`) : '',
          message: formData.message,
          email: formData.email,
        },
        'C5wz6GWLLjz7z6HFf'
      );

      setIsSubmitted(true);
      setFormData({ name: '', email: '', company: '', message: '' });
      setTimeout(() => {
        setShowPopup(false);
        setIsSubmitted(false);
      }, 3000);

    } catch (error) {
      console.error('EmailJS error:', error);
      alert('Failed to send. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-[#1A3A5C] to-[#0D2137] animate-gradient"></div>
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-gold/10 animate-gradient"></div>
        </div>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
          <div className={`flex items-center gap-2 text-sm mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Link to="/" className="text-gray-400 hover:text-gold transition-colors cursor-pointer">Home</Link>
            <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-s-line text-gray-500`}></i>
            <span className="text-gold">{t('nav.startInvesting')}</span>
          </div>
          <div className="max-w-3xl">
            <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-12 h-px bg-gold"></div>
              <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('investPage.hero.label')}</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-7xl text-white leading-tight mb-6">
              {t('investPage.hero.title1')} <span className="text-gold">{t('investPage.hero.title2')}</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed mb-6">
              {t('investPage.hero.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Image Slider - Auto Play */}
      <section className="relative bg-navy py-0">
        <div className="relative h-[700px] overflow-hidden">
          {investments.map((inv, index) => (
            <div
              key={inv.key}
              className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
            >
              <img
                src={inv.image}
                alt={t(`investPage.investments.${inv.key}`)}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span
                  className="text-white text-3xl md:text-5xl font-serif text-center px-8"
                  style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.8)' }}
                >
                  {t(`investPage.investments.${inv.key}`)}
                </span>
              </div>
            </div>
          ))}

          {/* Dots only */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {investments.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${currentSlide === i ? 'bg-gold w-6' : 'bg-white/50 w-1.5'}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Investment List */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-px bg-gold"></div>
              <span className="text-gold text-sm font-medium tracking-widest uppercase">
                {isRTL ? 'أنشطة الرخصة' : 'License Activities'}
              </span>
              <div className="w-12 h-px bg-gold"></div>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-navy">
              {isRTL ? 'فرص' : 'Investment'} <span className="text-gold">{isRTL ? 'الاستثمار' : 'Opportunities'}</span>
            </h2>
            <p className="text-gray-500 mt-4 text-sm md:text-base">
              {isRTL ? 'انقر على أي استثمار لإرسال استفسارك' : 'Click on any investment to send your inquiry'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {investments.map((inv) => (
              <button
                key={inv.key}
                onClick={() => handleInvestmentClick(inv.key)}
                className="group relative bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-gold/30 hover:shadow-xl transition-all duration-500 text-left cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={inv.image}
                    alt={t(`investPage.investments.${inv.key}`)}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent"></div>
                  <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} w-10 h-10 bg-gradient-to-br from-gold to-gold-dark rounded-xl flex items-center justify-center`}>
                    <i className={`${inv.icon} text-navy text-lg`}></i>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <span className="text-white text-xs font-medium bg-gold/20 px-2 py-1 rounded-full border border-gold/30">
                      {isRTL ? 'انقر للاستثمار' : 'Click to Invest'}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <p className={`text-navy font-semibold text-sm md:text-base leading-snug flex-1 ${isRTL ? 'text-right' : ''}`}>
                      {t(`investPage.investments.${inv.key}`)}
                    </p>
                    <div className={`w-8 h-8 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0 ${isRTL ? 'mr-3' : 'ml-3'} group-hover:bg-gold transition-colors duration-300`}>
                      <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line text-gold group-hover:text-navy text-sm`}></i>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-green-600 text-xs font-medium">
                      {isRTL ? 'فعال' : 'Active'}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`bg-white rounded-3xl p-6 md:p-8 w-full max-w-lg shadow-2xl relative ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <button
              onClick={() => setShowPopup(false)}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <i className="ri-close-line text-gray-600"></i>
            </button>

            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-2">{isRTL ? 'تم الإرسال!' : 'Sent Successfully!'}</h3>
                <p className="text-gray-500 text-sm">{t('investPage.form.success')}</p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h3 className="font-serif text-xl md:text-2xl text-navy mb-2">{t('investPage.form.title')}</h3>
                  <p className="text-gold text-sm font-medium">
                    {selectedInvestment && t(`investPage.investments.${selectedInvestment}`)}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('investPage.form.name')} *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all text-sm"
                        placeholder={t('investPage.form.namePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('investPage.form.email')} *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all text-sm"
                        placeholder={t('investPage.form.emailPlaceholder')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('investPage.form.company')}</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all text-sm"
                      placeholder={t('investPage.form.companyPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('investPage.form.message')} *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all resize-none text-sm"
                      placeholder={t('investPage.form.messagePlaceholder')}
                    />
                  </div>

                  <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`flex-1 py-3 bg-gradient-to-r from-gold to-gold-dark text-navy font-semibold rounded-xl hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70 ${isRTL ? 'flex-row-reverse' : ''}`}
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          {t('investPage.form.sending')}
                        </>
                      ) : (
                        <>
                          {t('investPage.form.submit')}
                          <i className={`ri-send-plane-line ${isRTL ? 'rotate-180' : ''}`}></i>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowPopup(false)}
                      className="px-6 py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-all duration-300 text-sm"
                    >
                      {t('investPage.form.close')}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}